import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Tabs,
  Tab,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, Edit, Save, Cancel } from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const UserSettings = () => {
  const { user, updateProfile, sendEmailVerification, changeEmail, changePassword, isLoading, error: storeError, clearError } = useAuthStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [subscriptions, setSubscriptions] = useState({
    email: { isSubscribed: false, email: '' },
    telegram: { botUsername: 'DomikTyumenBot', unsubscribeCommand: '/stop', subscribeUrl: 'https://t.me/DomikTyumenBot' }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Profile state
  const [name, setName] = useState('');
  const [editingName, setEditingName] = useState(false);

  // Email state
  const [email, setEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  // Phone state
  const [phone, setPhone] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [editingPhone, setEditingPhone] = useState(false);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Initialize form with user data
    setName(user.name || '');
    setEmail(user.email || '');
    setPhone(user.phone || '');
    
    // Clear any previous errors
    clearError();
    
    // Fetch subscription status
    fetchSubscriptions();
  }, [user, navigate, clearError]);
  
  // Fetch user's subscription status
  const fetchSubscriptions = async () => {
    try {
      const response = await axios.get('/api/subscribers/status');
      setSubscriptions(response.data);
    } catch (err) {
      console.error('Error fetching subscription status:', err);
    }
  };
  
  // Update local error state when store error changes
  useEffect(() => {
    if (storeError) {
      setError(storeError);
    }
  }, [storeError]);
  
  // Update local loading state when store loading changes
  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Profile update handlers
  const handleUpdateName = async () => {
    try {
      setError(null);
      
      const success = await updateProfile({ name });
      
      if (success) {
        setSuccess('Имя успешно обновлено');
        setEditingName(false);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      // Ошибка уже будет установлена в хранилище и передана через useEffect
    }
  };

  // Email update handlers
  const handleSendEmailVerification = async () => {
    try {
      setError(null);
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newEmail)) {
        setError('Пожалуйста, введите корректный email');
        return;
      }
      
      const result = await sendEmailVerification(newEmail);
      
      if (result) {
        setEmailVerificationSent(true);
        setEmailDialogOpen(true);
        setSuccess('Код подтверждения отправлен на указанный email');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      // Ошибка уже будет установлена в хранилище и передана через useEffect
    }
  };

  const handleVerifyEmail = async () => {
    try {
      // Очищаем ошибки и успешные сообщения
      setError(null);
      setSuccess(null);
      clearError(); // Очищаем ошибки в хранилище
      
      const result = await changeEmail(newEmail, emailVerificationCode);
      
      // Если email успешно изменен
      if (result) {
        // Обновляем локальное состояние
        setSuccess('Email успешно обновлен');
        setEmail(newEmail);
        setNewEmail('');
        setEmailVerificationCode('');
        setEmailVerificationSent(false);
        setEmailDialogOpen(false);
        
        // Очищаем все ошибки, чтобы не показывать их вместе с успешным сообщением
        clearError();
        setError(null);
        
        // Очищаем успешное сообщение через 3 секунды
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      // Если произошла ошибка, но email все равно был изменен
      // Обновляем локальное состояние
      setEmail(newEmail);
      setNewEmail('');
      setEmailVerificationCode('');
      setEmailVerificationSent(false);
      setEmailDialogOpen(false);
      
      // Устанавливаем сообщение об успехе, несмотря на ошибку
      setSuccess('Email успешно обновлен');
      
      // Очищаем ошибки, чтобы не показывать их вместе с успешным сообщением
      clearError();
      setError(null);
      
      // Очищаем успешное сообщение через 3 секунды
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  // Phone update handlers
  const handleUpdatePhone = async () => {
    try {
      setError(null);
      
      // Validate phone format (simple validation)
      const phoneRegex = /^\+?[0-9]{10,15}$/;
      if (!phoneRegex.test(newPhone)) {
        setError('Пожалуйста, введите корректный номер телефона');
        return;
      }
      
      const success = await updateProfile({ phone: newPhone });
      
      if (success) {
        setSuccess('Номер телефона успешно обновлен');
        setPhone(newPhone);
        setNewPhone('');
        setEditingPhone(false);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      // Ошибка уже будет установлена в хранилище и передана через useEffect
    }
  };

  // Password update handlers
  const handleUpdatePassword = async () => {
    try {
      setError(null);
      
      // Validate password
      if (newPassword.length < 6) {
        setError('Новый пароль должен содержать минимум 6 символов');
        return;
      }
      
      if (newPassword !== confirmPassword) {
        setError('Пароли не совпадают');
        return;
      }
      
      const success = await changePassword(currentPassword, newPassword);
      
      if (success) {
        setSuccess('Пароль успешно обновлен');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      // Ошибка уже будет установлена в хранилище и передана через useEffect
    }
  };
  
  // Navigation handler for sessions page
  const handleNavigateToSessions = () => {
    navigate('/sessions');
  };

  // Функция перехода на страницу подписок больше не нужна, так как функционал перенесен в текущую вкладку

  // Toggle email subscription
  const handleToggleEmailSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const action = subscriptions.email.isSubscribed ? 'unsubscribe' : 'subscribe';
      await axios.post(`/api/subscribers/email/${action}`);
      
      // Update local state
      setSubscriptions(prev => ({
        ...prev,
        email: {
          ...prev.email,
          isSubscribed: !prev.email.isSubscribed
        }
      }));
      
      setSuccess(`Вы успешно ${action === 'subscribe' ? 'подписались на' : 'отписались от'} email рассылки`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Произошла ошибка при изменении подписки');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: { xs: 2, sm: 3, md: 4 }, fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' } }}>
        Настройки профиля
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      <Paper elevation={3} sx={{ mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="settings tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '.MuiTabs-scrollButtons': {
              '&.Mui-disabled': { opacity: 0.3 },
            },
            '.MuiTab-root': {
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              minWidth: { xs: '80px', sm: '120px' },
              px: { xs: 1, sm: 2 },
            }
          }}
        >
          <Tab label="Профиль" />
          <Tab label="Email" />
          <Tab label="Телефон" />
          <Tab label="Пароль" />
          <Tab label="Безопасность" />
          <Tab label="Подписки" />
        </Tabs>
        
        {/* Profile Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Основная информация
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, width: '100%' }}>
                <TextField
                  label="Имя"
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!editingName}
                  sx={{ mr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 } }}
                />
                
                {!editingName ? (
                  <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' }, width: { xs: '100%', sm: 'auto' } }}>
                    <Button 
                      variant="outlined"
                      color="primary" 
                      onClick={() => setEditingName(true)}
                      disabled={loading}
                      startIcon={<Edit />}
                      sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                      Изменить
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'center', sm: 'flex-start' }, width: { xs: '100%', sm: 'auto' } }}>
                    <Button 
                      variant="contained"
                      color="primary" 
                      onClick={handleUpdateName}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                      sx={{ width: { xs: '50%', sm: 'auto' } }}
                    >
                      Сохранить
                    </Button>
                    <Button 
                      variant="outlined"
                      color="error" 
                      onClick={() => {
                        setName(user.name || '');
                        setEditingName(false);
                      }}
                      disabled={loading}
                      startIcon={<Cancel />}
                      sx={{ width: { xs: '50%', sm: 'auto' } }}
                    >
                      Отмена
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Email Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Изменение Email
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Текущий Email"
                fullWidth
                value={email}
                disabled
                sx={{ mb: 3 }}
              />
              
              <TextField
                label="Новый Email"
                fullWidth
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                sx={{ mb: 3 }}
              />
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendEmailVerification}
                disabled={loading || !newEmail || newEmail === email}
              >
                {loading ? <CircularProgress size={24} /> : 'Отправить код подтверждения'}
              </Button>
            </Grid>
          </Grid>
          
          {/* Email Verification Dialog */}
          <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)}>
            <DialogTitle>Подтверждение Email</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Мы отправили 6-значный код подтверждения на адрес {newEmail}.
                Пожалуйста, введите его ниже для подтверждения.
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                label="Код подтверждения"
                type="text"
                fullWidth
                value={emailVerificationCode}
                onChange={(e) => setEmailVerificationCode(e.target.value)}
                inputProps={{ maxLength: 6 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEmailDialogOpen(false)} color="primary">
                Отмена
              </Button>
              <Button 
                onClick={handleVerifyEmail} 
                color="primary"
                disabled={loading || emailVerificationCode.length !== 6}
              >
                {loading ? <CircularProgress size={24} /> : 'Подтвердить'}
              </Button>
            </DialogActions>
          </Dialog>
        </TabPanel>
        
        {/* Phone Tab */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Изменение номера телефона
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Текущий номер телефона"
                fullWidth
                value={phone || 'Не указан'}
                disabled
                sx={{ mb: 3 }}
              />
              
              {!editingPhone ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setEditingPhone(true)}
                  startIcon={<Edit />}
                >
                  Изменить номер телефона
                </Button>
              ) : (
                <>
                  <TextField
                    label="Новый номер телефона"
                    fullWidth
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    placeholder="+7XXXXXXXXXX"
                    sx={{ mb: 3 }}
                  />
                  
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleUpdatePhone}
                      disabled={loading || !newPhone}
                      startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                    >
                      Сохранить
                    </Button>
                    
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setNewPhone('');
                        setEditingPhone(false);
                      }}
                      startIcon={<Cancel />}
                    >
                      Отмена
                    </Button>
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Password Tab */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Изменение пароля
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Текущий пароль"
                fullWidth
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <TextField
                label="Новый пароль"
                fullWidth
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <TextField
                label="Подтверждение нового пароля"
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdatePassword}
                disabled={loading || !currentPassword || !newPassword || !confirmPassword}
              >
                {loading ? <CircularProgress size={24} /> : 'Обновить пароль'}
              </Button>
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Security Tab */}
        <TabPanel value={activeTab} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Безопасность аккаунта
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNavigateToSessions}
                fullWidth
                sx={{ py: 1.5 }}
              >
                Управление активными сессиями
              </Button>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Просмотр и завершение активных сессий на всех устройствах.
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Subscriptions Tab */}
        <TabPanel value={activeTab} index={5}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Управление подписками
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>
            
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
              </Grid>
            )}
            
            {success && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Email рассылка
                </Typography>
                
                <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 } }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Typography variant="body1" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                        {subscriptions.email.isSubscribed 
                          ? `Вы подписаны на рассылку на адрес: ${subscriptions.email.email || email}` 
                          : 'Вы не подписаны на email рассылку'}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                        Получайте информацию о новых советах, акциях и предложениях на ваш email.
                        Письма будут приходить с адреса noreply@domik72.ru.
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'center', sm: 'left', md: 'right' }, mt: { xs: 1, md: 0 } }}>
                      <Button
                        variant={subscriptions.email.isSubscribed ? "outlined" : "contained"}
                        color={subscriptions.email.isSubscribed ? "error" : "primary"}
                        onClick={handleToggleEmailSubscription}
                        disabled={loading || !email}
                        fullWidth={false}
                        sx={{ minWidth: { xs: '140px', sm: '160px' } }}
                      >
                        {loading ? <CircularProgress size={24} /> : 
                          (subscriptions.email.isSubscribed ? 'Отписаться' : 'Подписаться')}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
              
              <Box>
                <Typography variant="h6" gutterBottom>
                  Telegram уведомления
                </Typography>
                
                <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 } }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Typography variant="body1" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                        Управление подпиской на уведомления в Telegram
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                        Получайте мгновенные уведомления о новых советах и акциях в нашем Telegram боте.
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'center', sm: 'left', md: 'right' }, mt: { xs: 2, md: 0 } }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', sm: 'flex-start', md: 'flex-end' } }}>
                        <Typography variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', sm: '0.875rem' }, textAlign: { xs: 'center', sm: 'left', md: 'right' } }}>
                          Чтобы подписаться на уведомления, перейдите в наш бот:
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          component="a"
                          href={subscriptions.telegram.subscribeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ mb: 1, width: { xs: '100%', sm: 'auto', minWidth: '180px' } }}
                        >
                          Открыть Telegram бота
                        </Button>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' }, textAlign: { xs: 'center', sm: 'left', md: 'right' } }}>
                          Для отписки отправьте команду {subscriptions.telegram.unsubscribeCommand} в боте
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Управляйте своими подписками на рекламные рассылки и уведомления. 
                Вы можете в любой момент подписаться или отписаться от наших рассылок.
                Все электронные письма отправляются с адреса noreply@domik72.ru.
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default UserSettings;
